import { type ReactNode } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useIsSecuraPathStaff } from "@/hooks/useFrameworkMapping";
import { Loader2 } from "lucide-react";

interface StaffRouteProps {
  children?: ReactNode;
  /**
   * Where to send non-staff sessions. Defaults to the client portal login —
   * a destination that is NEVER itself staff-gated. CIR-2026-357 brings the
   * whole `/clients` console under StaffRoute, so the previous `/clients`
   * default would loop (StaffRoute → /clients → StaffRoute …).
   */
  redirectTo?: string;
}

/**
 * CIR-2026-147: Route guard for SecuraPath staff-only pages.
 * Wraps routes that should only be accessible to SecuraPath staff.
 * Non-staff users are silently redirected to `redirectTo`.
 *
 * Uses useIsSecuraPathStaff() hook (React Query cached, shared with AppSidebar).
 * Nests inside ProtectedRoute: session check → staff check → page render.
 */
export function StaffRoute({ children, redirectTo = "/client-login" }: StaffRouteProps) {
  const { data: isStaff, isLoading } = useIsSecuraPathStaff();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isStaff) {
    return <Navigate to={redirectTo} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
